import {
  FocusEvent,
  FunctionComponent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { PatronLocationFinderSearchSuggestion } from 'components/patron-location/PatronLocationFinderSearch';
import { SearchInput } from 'omakase';
import { debounce as _debounce } from 'es-toolkit/compat';
import { getPlacesAutosuggestionsViaRequest } from 'utils/locationUtils';
import { useIsVisglMapsEnabled } from 'featureFlags/useIsVisglMapsEnabled';

interface PatronLocationFinderSearchInputProps {
  centerLatLng?: google.maps.LatLng;
  iconClear?: ReactNode;
  iconStart?: ReactNode;
  initialValue?: string;
  onInputBlur?(event: FocusEvent<HTMLInputElement>): void;
  onInputFocus?(event: FocusEvent<HTMLInputElement>): void;
  onInputKeyDown?(event: KeyboardEvent<HTMLInputElement>): void;
  selected?: boolean;
  sessionToken?: google.maps.places.AutocompleteSessionToken;
  setSuggestions(suggestions: PatronLocationFinderSearchSuggestion[]): void;
}

export const PatronLocationFinderSearchInput: FunctionComponent<
  PatronLocationFinderSearchInputProps
> = ({
  centerLatLng,
  initialValue = '',
  onInputBlur,
  onInputFocus,
  onInputKeyDown,
  selected = false,
  sessionToken,
  setSuggestions,
}) => {
  // TODO(visgl-burn-in): remove the `useNewApi` flag and use only the modern
  // (vis.gl) autosuggestion data source once
  // CONSUMER_ENABLE_GOOGLE_MAPS_API_UPDATE is fully rolled out.
  const useNewApi = useIsVisglMapsEnabled();
  const [activeRequests, setActiveRequests] = useState(0);
  const [initialFocus, setInitialFocus] = useState(false);
  const [query, setQuery] = useState(initialValue);
  const textInputRef = useRef<HTMLInputElement>(null);
  const delayedPerformQuery = useCallback(safelyFetchPlaces, []);

  useEffect(() => {
    if (selected && initialFocus) {
      textInputRef?.current?.focus();
    }
  }, [selected, initialFocus]);

  const handleClear = () => {
    setSuggestions([]);
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (onInputFocus) {
      onInputFocus(event);
    }
    setInitialFocus(true);
  };

  const handleRequestStart = () => setActiveRequests(activeRequests + 1);
  const handleRequestEnd = () => setActiveRequests(Math.max(0, activeRequests - 1));

  const performQuery = (value: string) => {
    const request: google.maps.places.AutocompletionRequest = {
      types: ['geocode'],
      input: value,
      sessionToken,
      // Bias suggestions toward the map center via a ~25km circle. `locationBias`
      // is the current API; the deprecated `location`/`radius` pair (deprecated
      // May 2023) is honored by neither the modern `AutocompleteSuggestion` nor
      // cleanly by the legacy `AutocompleteService`, so we build the bias here
      // once and both data-source paths forward it unchanged.
      locationBias: centerLatLng ? { center: centerLatLng, radius: 25000 } : undefined,
    };
    delayedPerformQuery(request, setSuggestions, handleRequestStart, handleRequestEnd, useNewApi);
  };

  const handleChange = (value: string) => {
    setQuery(value);
    if (value.trim() !== query) {
      performQuery(value.trim());
    }
  };

  return (
    <SearchInput
      fullWidth
      onBlur={onInputBlur}
      onChange={handleChange}
      onFocus={handleFocus}
      onKeyDown={onInputKeyDown}
      placeholder="Find a location"
      type="search"
      value={query}
      ref={textInputRef}
      size="large"
      IconButtonProps={{ onClick: handleClear }}
    />
  );
};

const safelyFetchPlaces = _debounce(
  async (
    request: google.maps.places.AutocompletionRequest,
    setSuggestions: (suggestions: PatronLocationFinderSearchSuggestion[]) => void,
    onRequestStart: () => void,
    onRequestEnd: () => void,
    useNewApi = false
  ) => {
    onRequestStart();
    if (request.input) {
      try {
        const places: google.maps.places.AutocompletePrediction[] =
          await getPlacesAutosuggestionsViaRequest(request, useNewApi);
        setSuggestions(
          places.map((place) => ({
            placeId: place.place_id,
            primaryText: place.structured_formatting.main_text,
            secondaryText: place.structured_formatting.secondary_text,
          }))
        );
        onRequestEnd();
      } catch (error) {
        // `ZERO_RESULTS` is an expected outcome here (fires per keystroke), so
        // stay silent for it; surface any other failure to the logger. No toast
        // (this fires per keystroke) and no `dispatch` is available at module
        // scope.
        if (String(error) !== 'ZERO_RESULTS') {
          const cause = error instanceof Error ? error : new Error(String(error));
          window.LOGGER?.error('Failed to fetch place autosuggestions', cause);
        }
        onRequestEnd();
      }
    } else {
      setSuggestions([]);
      onRequestEnd();
    }
  },
  500
);
